import React, { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { Box, Button, IconButton, Typography } from '@mui/material';
import * as actions from '../../../../store/actions';
import { EAccessRole, RootState } from '../../../../store/types';
import weekDayToText from '../../../../utils/weekDayToText';
import monthToText from '../../../../utils/monthToText';
import moment from 'moment';
import { i18n } from '../../../../translate/i18n';
import Spinner from '../../../../components/Spinner';
import DeleteRounded from '@mui/icons-material/DeleteRounded';
import confirm from '../../../../UI/Confirm/confirm';

type Props = {
  closeModal: CallableFunction,
  redirect: CallableFunction,
}

const ScheduledServicesByDayAndCompletedModal: React.FC<Props> = ({ closeModal, redirect }) => {

  const dispatch = useDispatch();
  const servicesByDayAndCompleted = useSelector((state: RootState) => state.scheduledServices.calendar.servicesByDayAndCompleted);
  const selectedEvent = useSelector((state: RootState) => state.scheduledServices.calendar.selectedEvent);
  const loadingServices = useSelector((state: RootState) => state.scheduledServices.calendar.loadingServicesByDayAndCompleted);
  const filters = useSelector((state: RootState) => state.scheduledServices.calendar.filters);
  const filteredSites = useSelector((state: any) => state.sites.selectedSitesPlaces);
  const loadingDelete = useSelector((state: RootState) => state.scheduledServices.loadingSoftDeleteService);

  const selectedAccess = useSelector((state: any) => state.auth.selectedAccess);

  const isAnalyst = selectedAccess.role === EAccessRole.analyst;
  const isContractor = selectedAccess.role === EAccessRole.contractor;
  const isInspector = selectedAccess.inspector && (isAnalyst || isContractor);

  const loading = loadingServices || loadingDelete;

  const styles = stylesFunc(selectedEvent.borderColor);
  const { areas, companies, equipmentGroups, equipmentModels, frequencies, places, serviceTypes, users } = filters;

  const fetchScheduledServices = useCallback(() => dispatch(actions.scheduledServicesFetchServicesByDayAndCompleted(selectedEvent.date, selectedEvent.completed, filteredSites, users, equipmentGroups, equipmentModels, areas, places, companies, serviceTypes, frequencies)), [selectedEvent.date, selectedEvent.completed, filteredSites, users, equipmentGroups, equipmentModels, areas, places, companies, serviceTypes, frequencies]);

  const fetchCalendarEvents = useCallback(() => dispatch(actions.scheduledServicesFetchCalendarEvents(selectedEvent.date, filteredSites, users, equipmentGroups, equipmentModels, areas, places, companies, serviceTypes, frequencies)), [selectedEvent.date, filteredSites, users, equipmentGroups, equipmentModels, areas, places, companies, serviceTypes, frequencies]);

  const softDeleteScheduledService = useCallback((scheduledServiceId: string) => dispatch(actions.softDeleteCalendarScheduledService(scheduledServiceId, selectedEvent.date, selectedEvent.completed, filteredSites, users, equipmentGroups, equipmentModels, areas, places, companies, serviceTypes, frequencies)), [selectedEvent.date, selectedEvent.completed, filteredSites, users, equipmentGroups, equipmentModels, areas, places, companies, serviceTypes, frequencies])
  
  const formatDateText = (date: Date) => {
    const day = date.getDate();
    const month = monthToText(date.getMonth())
    const year = date.getFullYear();

    return `${i18n.t('titleHeaderEvent.text')} ${day}, ${month}, ${year}`;
  }

  const formatWeekDay = (date: Date) => {
    const weekDay = weekDayToText(moment(date).day());
    return weekDay;
  }

  const ModalHeader: React.FC = () => {
    const date = new Date(selectedEvent.date);
    return (
      <Box sx={styles.headerContainer}>
        <EventAvailableIcon sx={{ color: selectedEvent.borderColor }} fontSize='medium' />
        <Box sx={styles.headerTextContainer}>
          <Box sx={styles.dateText}>{formatDateText(new Date(date))}</Box>
          <Box sx={{...styles.dateText, marginTop: '-10px'}}>{formatWeekDay(new Date(date))}</Box>
          <Box sx={styles.titleText}>{selectedEvent.title}</Box>
        </Box>
      </Box>
    );
  }

  useEffect(() => {
    if (selectedEvent.date !== "") fetchScheduledServices();
  }, [selectedEvent.date, selectedEvent.completed]);

  const handleDeleteScheduledService = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, scheduledServiceId: string) => {
    event.stopPropagation();
    confirm(i18n.t('scheduledServicesByUserAndDayModal.confirm'),
      {
        title: i18n.t('scheduledServicesByUserAndDayModal.title'),
        titleColor: 'red',
        disableBackdrop: true
      })
      .then(
        () => {
          console.log('rejeitado');
        },
        async () => {
          softDeleteScheduledService(scheduledServiceId);
        },
      )
  }

  return (
    <Box sx={styles.modalContainer}>
      { loading && <Spinner overlay size={40} />}
      <Box sx={styles.closeIconContainer}>
        <CloseIcon style={styles.closeIcon} onClick={() => closeModal()}/>
      </Box>
      <ModalHeader />
      {servicesByDayAndCompleted.map((service) => <Button key={service._id} sx={styles.cardContainer} onClick={() => redirect(service.equipment._id)}>
        <Box sx={{display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center'}}>
          <Typography>{service.equipmentModelName}</Typography>
          <IconButton disabled={isInspector} children={<DeleteRounded/>} onClick={(event) => handleDeleteScheduledService(event, service._id)}/>
        </Box>
        <Typography>{`${service.serviceType.name} ${service.frequency.name}`}</Typography>
        <Box sx={styles.dataContainer}>
          <Box sx={styles.label}>{i18n.t('scheduledServicesByUserAndDayModal.serialNumber') as string}</Box>
          <Box sx={styles.data}>{service.equipment.serialNumber}</Box>
        </Box>
        <Box sx={styles.dataContainer}>
          <Box sx={styles.label}>{i18n.t('scheduledServicesByUserAndDayModal.place') as string}</Box>
          <Box sx={styles.data}>{service.place.path}</Box>
        </Box>
        <Box sx={styles.dataContainer}>
          <Box sx={styles.label}>{i18n.t('scheduledServicesByUserAndDayModal.scheduledDate') as string}</Box>
          <Box sx={styles.data}>{new Date(service.scheduledDate).toLocaleDateString('pt-BR')}</Box>
        </Box>
        <Box sx={styles.dataContainer}>
          <Box sx={styles.label}>{i18n.t('scheduledServicesByUserAndDayModal.accountable') as string}</Box>
          <Box sx={styles.data}>{service.accountables[0].name}</Box>
        </Box>
      </Button>)}
    </Box>
  );
}

const stylesFunc = (borderColor: string) => ({
  modalContainer: {
    width: '400px',
    backgroundColor: '#F5F5F5',
    position: 'relative',
    paddingTop: '50px',
    paddingLeft: '30px',
    paddingRight: '20px',
  },
  closeIconContainer: {
    position: 'absolute',
    top: '10px',
    left: '15px',
  },
  closeIcon: {
   fontSize: '30px',
   cursor: 'pointer',
  },
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    marginLeft: '5px',
    marginBottom: '40px'
  },
  headerTextContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '10px'
  },
  dateText: {
    fontSize: '1.5rem'
  },
  titleText: {
    marginTop: "-5px",
    fontSize: '1rem', 
    color: borderColor,
  },
  cardContainer: {
    backgroundColor: "#fff",
    padding: "15px",
    borderLeftColor: borderColor,
    borderLeftWidth: "5px",
    borderLeftStyle: "solid",
    marginBottom: "20px",
    paddingTop: '7px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '360px'
  },
  descriptionContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  label: {
    color: "#828282",
    fontSize: "0.8rem"
  },
  dataContainer: {
    marginTop: "5px",
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    maxWidth: '300px'
  },
  data: {
    marginTop: "-3px",
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    direction: 'rtl',
    textAlign: 'left',
    maxWidth: '320px'
  },
});

export default ScheduledServicesByDayAndCompletedModal;

